import { cloneDeep, isArray, isBoolean, isFunction, isNumber } from 'lodash';
import { v4 as uuid } from 'uuid';
import { ILayerProps } from '@/version-types';
import { CoreAPI, ICore, isLayerProps } from '../core';
import { IOperate, OperateAPI } from '../operate';
import { IStage, StageAPI } from '../stage';
import { HISTORY_AFTER_WRITE_EVENT, HISTORY_BEFORE_CHANGE_EVENT, HISTORY_CHANGE_EVENT } from './event-name';
import { RealEdit } from './extensions/real-edit';
import { IHistoryCustomData, IHistoryDefaultData, IHistoryItem, IHistorySyncOptions } from './type';

export class History {
    private identity: string;
    private frameworkCore: ICore;
    private frameworkStage: IStage;
    private frameworkOperate: IOperate;

    /** 是否启用重做撤销 */
    private isEnableRedoUndo: boolean = true;
    /** 是否是主历史记录 */
    private isMain: boolean = true;
    /** 主历史记录数据 */
    private mainList: IHistoryItem[] = [];
    /** 主历史记录游标 */
    private mainCursor: number = -1;
    /** 从历史记录数据 */
    private slaveList: IHistoryItem[] = [];
    /** 从历史记录游标 */
    private slaveCursor: number = -1;
    /** 功能使用列表 */
    private funcSet: Set<string> = new Set();
    /** 自定义历史记录 */
    private customHistory: Record<string, any> = {};
    /** 协同编辑扩展 */
    realEdit: RealEdit;

    constructor(identity: string) {
        this.identity = identity;
        this.frameworkCore = CoreAPI(this.identity);
        this.frameworkStage = StageAPI(this.identity);
        this.frameworkOperate = OperateAPI(this.identity);
        this.realEdit = new RealEdit();
    }

    /** 销毁 */
    destroy() {
        this.isMain = true;
        this.mainList.length = 0;
        this.mainCursor = -1;
        this.slaveList.length = 0;
        this.slaveCursor = -1;
        this.funcSet.clear();
        this.emitChangeEvent();
    }

    /**
     * 注册自定义历史记录
     * @param key - 历史记录类型
     * @param options - 注册自定义历史记录钩子
     */
    register(type: string, options: IHistorySyncOptions) {
        console.log('ot register', type, options);
        if (this.customHistory[type]) {
            console.error(`${type} 历史记录类型已存在`);
            return;
        }
        if (isFunction(options)) {
            // 统一使用 IHistorySyncOptions 类型
            console.error(`${type} options 参数必须是 IHistorySyncOptions 类型`);
        } else {
            this.customHistory[type] = options.onRedoUndo;
        }
    }

    /** 发送历史记录变化事件 */
    private emitChangeEvent(data?: any, isCustom = false, isRedo = false, isUndo = false, historyItem?: IHistoryItem) {
        this.frameworkCore.events.emit(
            HISTORY_CHANGE_EVENT,
            {
                canRedo: this.canRedo(),
                canUndo: this.canUndo(),
                isMain: this.isMain,
                isRedo,
                isUndo,
                isCustom,
                data,
            },
            historyItem,
        );
    }

    /** 发送历史记录写入事件 */
    private emitAfterWriteEvent(historyData: any, isCustom: boolean, historyItem: IHistoryItem) {
        this.frameworkCore.events.emit(
            HISTORY_AFTER_WRITE_EVENT,
            {
                isMain: this.isMain,
                isCustom,
                data: historyData,
            },
            historyItem,
        );
    }

    /** 发送历史记录变化之前事件 */
    private emitBeforeChangeEvent(
        data: any,
        isCustom: boolean,
        isRedo: boolean,
        isUndo: boolean,
        historyItem: IHistoryItem,
    ) {
        this.frameworkCore.events.emit(
            HISTORY_BEFORE_CHANGE_EVENT,
            {
                data,
                isRedo,
                isUndo,
                isCustom,
                isMain: this.isMain,
            },
            historyItem,
        );
    }

    /** 默认重做撤销 */
    defaultRedoUndo(historyItem: IHistoryItem, isRedo: boolean, isUndo: boolean) {
        const data = historyItem.data as IHistoryDefaultData;
        if (data.newLayer && data.oldLayer) {
            const layer = cloneDeep(isRedo ? data.newLayer : data.oldLayer);
            this.frameworkStage.addOrUpdate(layer);
            if ((isRedo && data.newLayer.hidden) || (isUndo && data.oldLayer.hidden)) {
                this.frameworkOperate.delActiveLayer();
                return;
            }
            this.frameworkOperate.setActiveLayer(layer.id);
        } else if (data.newLayers && data.oldLayers) {
            const layers = cloneDeep(isRedo ? data.newLayers : data.oldLayers);
            for (let i = 0; i < layers.length; i++) {
                const layer = layers[i];
                this.frameworkStage.addOrUpdate(layer, false);
            }
            this.frameworkOperate.delActiveLayer();
            this.frameworkCore.rerender();
        }
    }

    /** 自定义重做撤销 */
    private customRedoUndo(historyItem: IHistoryItem, isRedo: boolean, isUndo: boolean) {
        const type = (historyItem.data as IHistoryCustomData).type;
        if (!this.customHistory[type]) {
            console.error(`${type} 历史记录类型不存在`);
            return;
        }
        this.customHistory[type]?.(historyItem, isRedo, isUndo);
    }

    /** 是否可以重做 */
    canRedo() {
        const data = this.isMain ? this.mainList : this.slaveList;
        const cursor = this.isMain ? this.mainCursor : this.slaveCursor;
        return cursor < data.length - 1 && this.isEnableRedoUndo;
    }

    /** 是否可以撤销 */
    canUndo() {
        const cursor = this.isMain ? this.mainCursor : this.slaveCursor;
        return cursor > -1 && this.isEnableRedoUndo;
    }

    /** 编辑器禁用操作 */
    private checkDisableOperate() {
        return !this.isEnableRedoUndo || (this.isMain && this.frameworkOperate.isDisableOperate());
    }

    /** 设置重做撤销 */
    setRedoUndo(enable: boolean = true) {
        this.isEnableRedoUndo = enable;
        this.emitChangeEvent();
    }

    /** 获取游标 */
    getCursor() {
        return this.isMain ? this.mainCursor : this.slaveCursor;
    }

    /** 获取是否启用重做撤销 */
    getIsEnableRedoUndo() {
        return this.isEnableRedoUndo;
    }

    /** 重做 */
    redo() {
        if (!this.canRedo()) return;
        if (this.checkDisableOperate()) return;
        this.frameworkOperate.delHoverId();
        const historyList = this.isMain ? this.mainList : this.slaveList;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.isMain ? this.mainCursor++ : this.slaveCursor++;
        const historyItem = cloneDeep(historyList[this.isMain ? this.mainCursor : this.slaveCursor]);
        this.emitBeforeChangeEvent(historyItem.data, historyItem.isCustom, true, false, historyItem);
        if (historyItem.isCustom) {
            this.customRedoUndo(historyItem, true, false);
        } else {
            this.defaultRedoUndo(historyItem, true, false);
        }
        this.emitChangeEvent(historyItem.data, historyItem.isCustom, true, false, historyItem);
        setTimeout(() => this.resetActiveStage(historyItem), 0);
    }

    /** 撤销 */
    undo() {
        if (!this.canUndo()) return;
        if (this.checkDisableOperate()) return;
        this.frameworkOperate.delHoverId();
        const historyData = this.isMain ? this.mainList : this.slaveList;
        const historyItem = cloneDeep(historyData[this.isMain ? this.mainCursor : this.slaveCursor]);
        this.emitBeforeChangeEvent(historyItem.data, historyItem.isCustom, false, true, historyItem);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.isMain ? this.mainCursor-- : this.slaveCursor--;
        if (historyItem.isCustom) {
            this.customRedoUndo(historyItem, false, true);
        } else {
            this.defaultRedoUndo(historyItem, false, true);
        }
        this.emitChangeEvent(historyItem.data, historyItem.isCustom, false, true, historyItem);
        setTimeout(() => this.resetActiveStage(historyItem), 0);
    }

    /** 重置激活舞台 */
    private resetActiveStage(historyItem: IHistoryItem) {
        const { uid, originIndex } = historyItem;
        if (uid !== this.frameworkCore.getUid()) {
            return;
        }
        const currentOriginIndex = this.frameworkStage.getActiveOriginIndex();
        if (currentOriginIndex !== originIndex) {
            if (this.frameworkCore.getRenderStage().indexOf(originIndex) !== -1) {
                const stageIndex = this.frameworkCore.convertOriginToStage(originIndex);
                this.frameworkStage.setActiveStageIndex(stageIndex);
                this.frameworkOperate.stageScroll.scrollToActiveStage(false);
            }
        }
    }

    /** 是否使用主历史记录 */
    isMainHistory() {
        return this.isMain;
    }

    /** 使用主历史记录 */
    useMain() {
        this.isMain = true;
        this.slaveCursor = -1;
        this.slaveList = [];
        this.emitChangeEvent();
    }

    /** 使用从历史记录 */
    useSlave() {
        this.isMain = false;
        this.emitChangeEvent();
    }

    /** 写入历史记录 */
    write<T>(data: IHistoryCustomData<T>, trackFunc?: string): void;
    write(oldLayer: ILayerProps, trackFunc?: string): void;
    write(oldLayer: ILayerProps, newLayer: ILayerProps, trackFunc?: string): void;
    write(oldLayers: ILayerProps[], trackFunc?: string): void;
    write(oldLayers: ILayerProps[], newLayers: ILayerProps[], trackFunc?: string): void;
    write(
        oldData: ILayerProps | ILayerProps[] | IHistoryCustomData,
        newData?: ILayerProps | ILayerProps[] | string | number,
        trackFunc?: string,
    ) {
        let historyOriginIndex = this.frameworkStage.getActiveOriginIndex();
        let isCustom = false;
        let historyData: any;
        let skipCheckOriginIndex = false;
        let needSyncRenderIds = false;
        const isWriteArray = isArray(oldData);
        if (isWriteArray) {
            const oldLayers = oldData;
            let newLayers: ILayerProps[] = [];
            if (isArray(newData)) {
                // 第五种重载
                newLayers = newData;
            } else {
                // 第四种重载
                for (let i = 0; i < oldLayers.length; i++) {
                    const layer = oldLayers[i];
                    const newLayer = this.frameworkCore.getLayer(layer.id, true);
                    newLayers.push(newLayer);
                }
            }
            historyData = {
                oldLayers,
                newLayers,
            };
            if (newLayers.length > 0) {
                const curOriginIndex = this.frameworkCore.tryGetLayerOriginIndex(newLayers[0].id);
                if (isNumber(curOriginIndex)) {
                    historyOriginIndex = curOriginIndex;
                }
            }
        } else {
            // console.log('write', oldLayer, newLayer, trackFunc, isLayerProps(oldLayer));
            if (!isLayerProps(oldData)) {
                // 第一种重载, 自定义数据的处理
                const type = oldData.type;
                if (!this.customHistory[type]) {
                    console.error(`${type} 历史记录类型不存在`);
                    return;
                }
                if (isNumber(oldData.originIndex)) {
                    historyOriginIndex = oldData.originIndex;
                }
                isCustom = true;
                historyData = oldData;
                trackFunc = oldData.trackFunc;
                if (isBoolean(oldData.skipCheckOriginIndex)) {
                    skipCheckOriginIndex = oldData.skipCheckOriginIndex;
                }
                if (isBoolean(oldData.needSyncRenderIds)) {
                    needSyncRenderIds = oldData.needSyncRenderIds;
                }
            } else {
                const oldLayer = oldData;
                // 第二/三种重载
                const newLayer = isLayerProps(newData) ? newData : this.frameworkCore.getLayer(oldLayer.id, true);
                if (!newLayer) {
                    console.error('newLayer 不存在');
                    return;
                }
                historyData = {
                    oldLayer,
                    newLayer,
                };
                const curOriginIndex = this.frameworkCore.tryGetLayerOriginIndex(newLayer.id);
                if (isNumber(curOriginIndex)) {
                    historyOriginIndex = curOriginIndex;
                }
            }
        }
        const historyList = this.isMain ? this.mainList : this.slaveList;
        const cursor = this.isMain ? this.mainCursor : this.slaveCursor;
        historyList.length = cursor + 1;
        const time = Date.now();
        const historyItem: IHistoryItem = {
            id: uuid(),
            time,
            serverTime: time,
            data: cloneDeep(historyData),
            isCustom,
            trackFunc,
            uid: this.frameworkCore.getUid(),
            originIndex: historyOriginIndex,
            isRealEditMode: false,
            cursor: cursor + 1,
            skipCheckOriginIndex,
            needSyncRenderIds,
        };
        historyList.push(historyItem);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.isMain ? this.mainCursor++ : this.slaveCursor++;
        this.emitAfterWriteEvent(historyData, isCustom, historyItem);
        this.emitChangeEvent();
    }

    /** 历史记录写入之后 */
    onAfterWrite(
        fn: (args: { isMain: boolean; data: any; isCustom: boolean }, historyItem: IHistoryItem) => void,
        weight = 0,
    ) {
        return this.frameworkCore.events.on(HISTORY_AFTER_WRITE_EVENT, fn, weight);
    }

    /** 历史记录变化监听 */
    onChange(
        fn: (
            args: {
                data: any;
                canRedo: boolean;
                canUndo: boolean;
                isMain: boolean;
                isCustom: boolean;
                isRedo: boolean;
                isUndo: boolean;
            },
            historyItem?: IHistoryItem,
        ) => void,
        weight = 0,
    ) {
        return this.frameworkCore.events.on(HISTORY_CHANGE_EVENT, fn, weight);
    }

    /** 历史记录变化之前 */
    onBeforeChange(
        fn: (
            args: { isMain: boolean; data: any; isCustom: boolean; isRedo: boolean; isUndo: boolean },
            historyItem: IHistoryItem,
        ) => void,
        weight = 0,
    ) {
        return this.frameworkCore.events.on(HISTORY_BEFORE_CHANGE_EVENT, fn, weight);
    }

    /** 获取功能使用列表 */
    getFuncUseList() {
        const items = this.mainList;
        const funcUseList: string[] = [];
        console.log('func items', items);
        try {
            for (let i = 0; i < items.length; i++) {
                const { trackFunc } = items[i];
                if (trackFunc) {
                    if (Array.isArray(trackFunc)) {
                        funcUseList.push(...trackFunc);
                    } else {
                        funcUseList.push(trackFunc);
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }
        return Array.from(new Set([...funcUseList, ...Array.from(this.funcSet)])).join(',');
    }

    /** 添加功能使用 */
    pushFunc(fun: string) {
        if (fun) this.funcSet.add(fun);
    }
}
